import { useEffect, useMemo } from 'react';
import { useMap, useNavermaps } from 'react-naver-maps';
import { useLocation, useNavigate } from 'react-router';
import {
	DEFAULT_STATION_MARKER_Z_INDEX,
	SELECTED_STATION_MARKER_Z_INDEX,
	SELECTED_STATION_SHEET_HEIGHT,
} from '@/shared/consts';
import { useBoundStore } from '@/shared/store';
import {
	createClusterMarkerHtml,
	createMarkerCluster,
	createStationMarkerHtml,
	focusMapPosition,
} from '../lib';
import useNaverMapStore from '../model/naverMapStore';
import type { StationWithSaving } from '../model/stationWithSaving.schema';
import useIsZoomedOut from '../model/useIsZoomedOut';

const CLUSTER_MARKER_SIZES = [40, 50, 60];

const getBaseStationMarkerLabel = (
	isBaseStation: boolean,
): string | undefined => {
	if (!isBaseStation) return undefined;
	return '가까운 주유소';
};

const getSelectedMarkerTargetY = (mapRect: DOMRect) => {
	const estimatedSheetTop = window.innerHeight - SELECTED_STATION_SHEET_HEIGHT;
	const sheetTopWithinMap = Math.min(
		mapRect.height,
		Math.max(0, estimatedSheetTop - mapRect.top),
	);

	return sheetTopWithinMap / 2;
};

const hasCoords = (station: StationWithSaving): boolean =>
	station.coorX !== null && station.coorY !== null;

const NaverMapStationMarker = () => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const sortedStations = useNaverMapStore((store) => store.sortedStations);
	const minZoomLevel = useNaverMapStore((state) => state.minZoomLevel);
	const selectedStationId = useNaverMapStore(
		(state) => state.selectedStationId,
	);
	const dispatch = useBoundStore((store) => store.dispatch);
	const setSelectedStationId = useNaverMapStore(
		(state) => state.setSelectedStationId,
	);

	const map = useMap();
	const navermaps = useNavermaps();
	const isZoomedOut = useIsZoomedOut();

	const MarkerClusteringClass = useMemo(
		() =>
			createMarkerCluster({
				maps: navermaps,
			}),
		[navermaps],
	);

	const clusterIcons = useMemo(
		() =>
			CLUSTER_MARKER_SIZES.map((size) => ({
				content: createClusterMarkerHtml(size),
				size: new navermaps.Size(size, size),
				anchor: new navermaps.Point(size / 2, size / 2),
			})),
		[navermaps],
	);

	useEffect(() => {
		if (isZoomedOut || !sortedStations.length) return;

		const focusStationMarker = (position: naver.maps.LatLng) => {
			const mapElement = map.getElement?.() as HTMLElement | undefined;
			const mapRect = mapElement?.getBoundingClientRect();

			if (!mapRect) {
				map.panTo(position);
				return;
			}

			focusMapPosition({
				map,
				position,
				targetY: getSelectedMarkerTargetY(mapRect),
			});
		};

		const markers = sortedStations.filter(hasCoords).map((station) => {
			const isSelected = station.id === selectedStationId;
			const markerLabel = getBaseStationMarkerLabel(!!station.isNearestStation);
			const position = new navermaps.LatLng(station.coorY, station.coorX);
			const marker = new navermaps.Marker({
				position,
				icon: {
					content: createStationMarkerHtml({
						price: station.price,
						savings: station.savings,
						isSelected,
						stationLogo: station.stationLogo,
						label: markerLabel,
					}),
					anchor: new navermaps.Point(50, 61),
				},
				clickable: true,
				zIndex: isSelected
					? SELECTED_STATION_MARKER_Z_INDEX
					: DEFAULT_STATION_MARKER_Z_INDEX,
			});

			const listener = marker.addListener('click', () => {
				setSelectedStationId(station.id);
				focusStationMarker(position);
				navigate(`/stations/${station.id}`, {
					replace: /^\/stations\//.test(pathname),
				});
				dispatch({ type: 'clickMarker' });
			});

			return { marker, listener };
		});

		const clustering = new MarkerClusteringClass({
			map,
			markers: markers.map(({ marker }) => marker),
			maxZoom: minZoomLevel,
			gridSize: 200,
			minClusterSize: 1,
			disableClickZoom: false,
			icons: clusterIcons,
			indexGenerator: [10, 50],
			stylingFunction: (clusterMarker: naver.maps.Marker, count: number) => {
				const element = clusterMarker.getElement();
				const span = element?.querySelector('span');
				if (span) span.textContent = String(count);
			},
		});

		return () => {
			clustering.setMap(null);
			markers.forEach(({ marker, listener }) => {
				navermaps.Event.removeListener(listener);
				marker.setMap(null);
			});
		};
	}, [
		isZoomedOut,
		sortedStations,
		selectedStationId,
		map,
		navermaps,
		minZoomLevel,
		MarkerClusteringClass,
		clusterIcons,
		navigate,
		pathname,
		setSelectedStationId,
		dispatch,
	]);

	return null;
};

export default NaverMapStationMarker;
